// NoteDetailPage — entry point for the original (pre-note-type era) progress note format.
//
// These notes predate the `note_type` field and the versioned note model entirely. They carry
// no `note_type` and no `version` property. The only discriminator is the presence of
// `prescriber_interventions` on the note object:
//   - absent  → Behavioral Specialist (BS) note  → BS_SCHEMA, renderBehavioralData
//   - present → Prescriber note                  → P_SCHEMA,  renderPrescribeData
//
// Routed at /patient/:patientId/note/:noteId (no version suffix).
// All notes created after the v2 note model — versioned (V1–V7) and explicitly typed
// (Care Management, Care Communication, Case Management, etc.) — are handled by
// NoteDetailPageV2 at the /v2 route instead.
import { connect } from "react-redux";
import { Form } from "formik";
import moment from "moment-timezone";
import React, { Component } from "react";

import {
  BS_INITIAL_VALUES,
  BS_SCHEMA,
  P_INITIAL_VALUES,
  P_SCHEMA,
} from "@/containers/Dashboard/Providers/PatientProfileView/schema/newNoteFormSchema";
import Button from "@/components/UI/Button";
import { isAuthorized } from "@/utils/authToken";
import { providerRequests } from "@/api/(requests)/provider";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { sharedRequests } from "@/api/(requests)/shared";
import { showLoader } from "@/redux/actions/loader";
import store from "@/redux/store";

import { BehavioralData } from "../components/BehavioralData";
import { PrescribeData } from "../components/PrescribeData";
import { SessionMetaData } from "../components/SessionMetaData";

import {
  createCommonNoteData,
  getNoteCard,
  getNoteFields,
  pickNoteDate,
  setBehavioralNoteData,
  setPrescriberNoteData,
  setSessionDate,
} from "./components/ProgressNotes";

class NoteDetailPageClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      behavioral: isAuthorized("provider:behavioral"),
      dateContext: moment(),
      id: this.props.match.params.noteId,
      note: null,
      patientId: this.props.match.params.patientId,
      patientName: "",
      prescribe: isAuthorized("provider:prescribe"),
      saveFunction: providerRequests.put_progress_note,
      showDatePicker: false,
    };
  }

  componentDidMount = async () => {
    try {
      let urlParams = { patientId: this.state.patientId };
      const context = { showLoader: false };
      const patientResponse = await sharedRequests.fetch_patient_details({ context, urlParams });
      const user = patientResponse.user;
      const searchParams = { note_id: this.state.id };
      const response = await sharedRequests.fetch_patient_progress_notes({
        searchParams,
        urlParams,
      });

      if (response) {
        if (response) {
          this.setState({
            note: response,
            patientName: user.first_name + " " + user.last_name,
            title: response.title,
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  submitHandler = async (formData) => {
    store.dispatch(showLoader());
    let data = await createCommonNoteData(this, formData, this.state.title, this.props.profile);

    if (this.state.behavioral) {
      setBehavioralNoteData(this, formData, this.state.title, data);
    } else {
      setPrescriberNoteData(this, formData, this.state.title, data);
    }
    data.id = this.state.id;
    let urlParams = { patientId: this.state.patientId };

    this.state
      .saveFunction({ data, urlParams })
      .then(() => {
        this.props.history.push(`/app/patient/${this.state.patientId}/profile`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  setBehavioralSpecialistNote = (note) => {
    let vals = { ...BS_INITIAL_VALUES };

    vals.title = note.title;
    vals.session_date = note.session_date;
    vals.session_from = note.session_from;
    vals.session_to = note.session_to;
    vals.patient_report = note.patient_report;
    vals.patient_task = note.patient_task;
    vals.interventions = note.interventions;
    vals.intervention_reaction = note.intervention_reaction;

    return vals;
  };

  setPrescriberNote = (note) => {
    let vals = { ...P_INITIAL_VALUES };

    vals.title = note.title;
    vals.session_date = note.session_date;
    vals.session_from = note.session_from;
    vals.session_to = note.session_to;
    vals.patient_reported = note.patient_reported;
    vals.prescriber_interventions = note.prescriber_interventions;
    vals.intervention_reaction = note.intervention_reaction;

    return vals;
  };

  getPickedDate = (dateString) => {
    pickNoteDate(this, dateString);
  };

  renderTitle = () => {
    return (
      <div className="ml-0 ml-lg-3">
        <b>{this.state.note.title}</b>
      </div>
    );
  };

  renderForm = (formikProps) => {
    let schema;

    if (this.state.note && !this.state.note.prescriber_interventions) {
      schema = BS_SCHEMA;
    } else if (this.state.note && this.state.note.prescriber_interventions) {
      schema = P_SCHEMA;
    }

    let dateStr = setSessionDate(this);

    return (
      <Form>
        {this.state.note && this.state.edit && getNoteFields(formikProps, schema, this)}

        {this.state.note && !this.state.edit && (
          <div style={{ padding: 20 }}>
            <SessionMetaData
              note={this.state.note}
              patientName={this.state.patientName}
              sessionDate={dateStr}
            />

            {!this.state.note.prescriber_interventions && <BehavioralData note={this.state.note} />}
            {this.state.note.prescriber_interventions && <PrescribeData note={this.state.note} />}
          </div>
        )}

        {this.state?.note?.provider_cp_credentials ? (
          <div className="col-12 col-lg-6">
            <div>
              <label className="Label">Provider Credentials</label>
              <p>{this.state?.note?.provider_cp_credentials}</p>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="d-flex justify-content-lg-end tw:justify-center">
          {this.state.edit && (
            <div>
              <Button
                className="Btn Btn--otl-pri Btn-sm mr-3"
                onClick={() =>
                  this.props.history.push(`/app/patient/${this.state.patientId}/profile`)
                }
                type="button"
              >
                Cancel
              </Button>

              <Button className="Btn Btn--pri Btn-sm" type="submit">
                Update Note
              </Button>
            </div>
          )}
        </div>
      </Form>
    );
  };

  renderControls = () => {};

  render() {
    let vals;

    if (this.state.note && !this.state.note.prescriber_interventions) {
      vals = this.setBehavioralSpecialistNote(this.state.note);
    } else if (this.state.note && this.state.note.prescriber_interventions) {
      vals = this.setPrescriberNote(this.state.note);
    }

    return <div>{this.state.note && getNoteCard(vals, null, this)}</div>;
  }
}

export const NoteDetailPage = connect((state) => ({ profile: selectProfile(state) }))(
  NoteDetailPageClass,
);
